/**
 * Which sign-in button was pressed, remembered just long enough to land on
 * the matching workspace.
 *
 * A routing hint in `sessionStorage` and nothing more: it never reaches the
 * server, because workspaces are derived from grants and a client that could
 * assert its own role would undo that. The route reads it through
 * `landingIntent`, which answers the same way twice — StrictMode renders
 * twice, and a second read that came back empty would land the user on the
 * chooser they were meant to skip.
 */

import { ROLE_KEYS, type RoleKey } from "@ondo/shared";

const STORAGE_KEY = "ondo.intendedRole";

/**
 * Which sign-in button the user clicked.
 *
 * This is a routing hint and nothing more. It is stored in sessionStorage —
 * client-controlled, therefore untrusted — and is read exactly once to choose
 * a landing workspace, then cleared.
 *
 * It must never be sent to the server, put in a header, or added to a token
 * claim. The whole point of deriving workspaces from grants is that the client
 * cannot assert its own role; persisting this as state would hand that back.
 */
export function rememberIntendedRole(role: RoleKey): void {
  // A new sign-in is a new intent, so whatever `landingIntent` cached for the
  // last one must not answer for this one.
  cached = undefined;

  try {
    sessionStorage.setItem(STORAGE_KEY, role);
  } catch {
    // Private browsing or a blocked store. The hint is optional — losing it
    // only means the user picks a workspace manually.
  }
}

/** Reads the hint and clears it, so it can never outlive one navigation. */
export function consumeIntendedRole(): RoleKey | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return isRoleKey(value) ? value : null;
  } catch {
    return null;
  }
}

let cached: { value: RoleKey | null } | undefined;

/**
 * The hint for this sign-in, answered the same way however often it is asked.
 *
 * `consumeIntendedRole` clears on read, which is the property that matters —
 * but a render is not guaranteed to happen exactly once. StrictMode renders
 * twice in development, and the route that reads this redirects rather than
 * holding state, so a second read must not come back empty and land the user
 * on the chooser they were meant to skip.
 *
 * The entry is still removed from storage on the first read; only the answer
 * is remembered, in module scope, and `rememberIntendedRole` clears it. So it
 * cannot outlive the sign-in that set it, which is the whole rule.
 */
export function landingIntent(): RoleKey | null {
  cached ??= { value: consumeIntendedRole() };
  return cached.value;
}

function isRoleKey(value: unknown): value is RoleKey {
  return typeof value === "string" && (ROLE_KEYS as readonly string[]).includes(value);
}
